import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import { Badge, Button } from '@/components/ui';
import type { Company } from '@/types/companyManagement.types';

const LIST_POOL_LIMIT = 2;
const LIST_ROLE_LIMIT = 2;

interface CompanyCardProps {
  company: Company;
  onEdit?: (company: Company) => void;
  onDelete?: (company: Company) => void;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

export const CompanyCard: React.FC<CompanyCardProps> = ({ company, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={clsx(
        'overflow-hidden rounded-lg border border-gray-200 transition-all duration-200 dark:border-dark-700',
        expanded && 'mb-4 mt-2 shadow-lg ring-1 ring-gray-200 dark:ring-gray-700'
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="flex w-full cursor-pointer items-center gap-2 bg-white px-2 py-1.5 pr-3 text-left transition-colors hover:bg-gray-50 dark:bg-dark-800 dark:hover:bg-dark-700"
      >
        <ChevronRight
          className={clsx(
            'h-5 w-5 flex-shrink-0 text-gray-400 transition-transform duration-200 dark:text-gray-500',
            expanded && 'rotate-90'
          )}
          aria-hidden
        />
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
          <span className="text-xs font-semibold text-primary-700 dark:text-primary-300">
            {company.name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {company.name}
            </span>
            <Badge variant={company.isInactive ? 'error' : 'success'} size="sm">
              {company.isInactive ? 'Inactive' : 'Active'}
            </Badge>
          </div>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
            {formatDate(company.startDate)} – {formatDate(company.endDate)}
          </p>

          <div className="mt-1 flex items-center gap-1 overflow-hidden">
            {company.poolIds.length > 0 ? (
              <>
                {company.poolIds.slice(0, LIST_POOL_LIMIT).map((p) => (
                  <Badge key={p.poolId} variant="primary" size="sm" className="shrink-0">
                    {p.pool}
                  </Badge>
                ))}
                {company.poolIds.length > LIST_POOL_LIMIT && (
                  <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                    +{company.poolIds.length - LIST_POOL_LIMIT}
                  </span>
                )}
              </>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500">No pools</span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-1 overflow-hidden">
            {company.groups.length > 0 ? (
              <>
                {company.groups.slice(0, LIST_ROLE_LIMIT).map((g) => (
                  <Badge key={g.code} variant="default" size="sm" className="shrink-0">
                    {g.name}
                  </Badge>
                ))}
                {company.groups.length > LIST_ROLE_LIMIT && (
                  <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                    +{company.groups.length - LIST_ROLE_LIMIT}
                  </span>
                )}
              </>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500">No roles</span>
            )}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-200 bg-gray-50 px-3 py-3 dark:border-dark-700 dark:bg-gray-900/50">
          <h4 className="mb-3 text-sm font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Company details
          </h4>

          <div className="grid grid-cols-2 gap-3">
            <DetailItem label="Start date" value={formatDate(company.startDate)} />
            <DetailItem label="End date" value={formatDate(company.endDate)} />
            <DetailItem label="Users" value={String(company.userCount)} />
            <DetailItem label="Last Login" value={formatDateTime(company.lastLoginAt)} />
          </div>

          <div className="mt-3">
            <p className="type-caption mb-2 font-medium text-gray-600 dark:text-gray-300">Pools</p>
            {company.poolIds.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {company.poolIds.map((p) => (
                  <Badge key={p.poolId} variant="primary" size="sm">
                    {p.pool}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">None</p>
            )}
          </div>

          <div className="mt-3">
            <p className="type-caption mb-2 font-medium text-gray-600 dark:text-gray-300">Roles</p>
            {company.groups.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {company.groups.map((g) => (
                  <Badge key={g.code} variant="default" size="sm">
                    {g.name}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">None</p>
            )}
          </div>

          {(onEdit || onDelete) && (
            <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-200 pt-4 dark:border-dark-600">
              {onEdit && (
                <Button variant="primary" size="sm" onClick={() => onEdit(company)}>
                  Edit company
                </Button>
              )}
              {onDelete && (
                <Button variant="danger" size="sm" onClick={() => onDelete(company)}>
                  Delete company
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="type-caption mb-0.5 font-medium text-gray-600 dark:text-gray-300">{label}</p>
      <p className="text-sm text-gray-900 dark:text-gray-100">{value}</p>
    </div>
  );
}
